import { DigiTransform } from '@digibricks-form-tools/md-to-json';
import {
  EssayQuestion,
  FillInBlankQuestion,
  Form,
  FormCategory,
  FormStatus,
  Heading,
  MatchingQuestion,
  MultipleChoiceQuestion,
  Question,
  QuestionProps,
  PreviousQuestionId,
  Section,
  SingleChoiceQuestion,
  Tag,
  FormCategoryStatus,
  PreviousSectionId,
  QuestionType,
} from '@module/form';
import type { ImportFormDto, ImportHeadingDto, ImportQuestionDto, ImportSectionDto } from './types';
import { UniqueEntityID } from '@digibricks/domains';
import { AiChangeType } from '@module/ai';
import { FormImportDtoToEntityConverter } from '../import-dto-to-entity';

export interface FormMarkdownToJsonConverterInput {
  markdownText: string;
}

export class FormMarkdownToJsonConverter {
  public static async execute(
    input: FormMarkdownToJsonConverterInput,
  ): Promise<[Form, Question<QuestionProps>[]]> {
    if (!input.markdownText || input.markdownText.trim() === '') {
      const emptyForm = Form.create({
        id: new UniqueEntityID().toString(),
        folderId: new UniqueEntityID().toString(),
        title: '',
        status: FormStatus.Active,
        description: '',
        sections: [],
        headings: [],
        tags: [],
      }).getValue();

      return [emptyForm, []];
    }

    const importDto = await FormMarkdownToJsonConverter.transformMarkdownToImportDto(
      input.markdownText,
    );

    return FormMarkdownToJsonConverter.mapImportDtoToFormAndQuestions(importDto, input);
  }

  public static executeFromImportDto(
    formId: string,
    importDto: ImportFormDto,
  ): [Form, Question<QuestionProps>[]] {
    return FormImportDtoToEntityConverter.execute(formId, importDto);
  }

  private static async transformMarkdownToImportDto(markdownText: string): Promise<ImportFormDto> {
    const digiTransform = new DigiTransform();
    return await digiTransform.transformMdContentToJson(markdownText);
  }

  private static mapImportDtoToFormAndQuestions(
    importDto: ImportFormDto,
    input: FormMarkdownToJsonConverterInput,
  ): [Form, Question<QuestionProps>[]] {
    const formId = importDto.id ?? new UniqueEntityID().toString();
    const folderId = importDto.folderId ?? new UniqueEntityID().toString();

    const sections = FormMarkdownToJsonConverter.mapSections(importDto.sections, formId);
    const headings = FormMarkdownToJsonConverter.mapHeadings(importDto.headings, formId);
    const tags = importDto.tags ? FormMarkdownToJsonConverter.mapTags(importDto.tags) : [];
    const category = FormCategory.create({
      id: importDto.category,
      key: importDto.category,
      status: FormCategoryStatus.Active,
      possibleQuestionCreationTypes: null,
    }).getValue();

    const form = Form.create({
      id: formId,
      folderId,
      title: importDto.title,
      status: FormStatus.Active,
      description: importDto.description ?? '',
      coverImage: importDto.coverImage,
      thumbnail: importDto.thumbnail ?? '',
      createdBy: importDto.createdBy,
      sections,
      headings,
      tags,
      category,
    }).getValue();

    const questions = FormMarkdownToJsonConverter.mapQuestions(importDto, formId);

    return [form, questions];
  }

  private static mapSections(sections: ImportSectionDto[], formId: string): Section[] {
    return sections.map((s) =>
      Section.create({
        id: s.id,
        title: s.title,
        description: s.description ?? '',
        formId,
        previousSectionId:
          s.previousSectionId === null || s.previousSectionId === undefined
            ? PreviousSectionId.None
            : s.previousSectionId,
        // NOTE: In this phase just support Add Change Type only
        aiChangeType: AiChangeType.Add,
      }).getValue(),
    );
  }

  private static mapHeadings(headings: ImportHeadingDto[], formId: string): Heading[] {
    return headings.map((h) =>
      Heading.create(
        {
          title: h.title,
          description: h.description ?? '',
          formId,
          sectionId: h.sectionId,
          nextQuestionId:
            h.nextQuestionId === null || h.nextQuestionId === undefined
              ? PreviousQuestionId.none.id.toString()
              : h.nextQuestionId,
          previousQuestionId:
            h.previousQuestionId === null || h.previousQuestionId === undefined
              ? PreviousQuestionId.none.id.toString()
              : h.previousQuestionId,
          nextHeadingId: h.nextHeadingId ?? PreviousQuestionId.none.id.toString(),
          previousHeadingId: h.previousHeadingId ?? PreviousQuestionId.none.id.toString(),
          // NOTE: Just support Add Change Type only
          aiChangeType: AiChangeType.Add,
        },
        new UniqueEntityID(h.id).toString(),
      ).getValue(),
    );
  }

  private static mapTags(tagKeys: string[]): Tag[] {
    return tagKeys.map((key) =>
      Tag.create({
        id: new UniqueEntityID().toString(),
        key,
      }).getValue(),
    );
  }

  private static mapQuestions(importDto: ImportFormDto, formId: string): Question<QuestionProps>[] {
    const questions: Question<QuestionProps>[] = [];

    for (const section of importDto.sections) {
      for (const q of section.questions) {
        questions.push(FormMarkdownToJsonConverter.mapQuestion(q, formId, section.id));
      }
    }

    return questions;
  }

  private static mapQuestion(
    q: ImportQuestionDto,
    formId: string,
    sectionId: string,
  ): Question<QuestionProps> {
    const baseData = {
      ...q,
      formId,
      sectionId,
      files: q.files ?? [],
      previousQuestionId:
        q.previousQuestionId === null || q.previousQuestionId === undefined
          ? PreviousQuestionId.none.id.toString()
          : q.previousQuestionId,
      nextQuestionId:
        q.nextQuestionId === null || q.nextQuestionId === undefined
          ? PreviousQuestionId.none.id.toString()
          : q.nextQuestionId,
      aiChangeType: AiChangeType.Add,
      // aiChangeType: q.aiChangeType as AiChangeType,
    };

    if (q.type === QuestionType.FillInBlank) {
      const fillInBlankData = {
        ...baseData,
        correctAnswers: (q.correctAnswers ?? []).map((c, idx) => ({
          id: c.id,
          answerId: c.answerId,
          score: c.score ?? 1,
          index: c.index ?? idx,
        })),
      };
      return FillInBlankQuestion.fromJson(fillInBlankData).getValue();
    }

    switch (q.type) {
      case QuestionType.Essay: {
        return EssayQuestion.fromJson(baseData).getValue();
      }
      case QuestionType.SingleChoice: {
        return SingleChoiceQuestion.fromJson(baseData).getValue();
      }
      case QuestionType.MultipleChoice: {
        return MultipleChoiceQuestion.fromJson(baseData).getValue();
      }
      case QuestionType.MultipleChoice: {
        return MultipleChoiceQuestion.fromJson(baseData).getValue();
      }
      case QuestionType.Matching: {
        return MatchingQuestion.fromJson(baseData).getValue();
      }
    }
  }
}
